import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Tag, 
  Button, 
  Space, 
  Input, 
  Select, 
  Row, 
  Col, 
  Typography,
  message,
  Spin
} from 'antd';
import { SearchOutlined, ReloadOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ticketService, Ticket } from '../services/ticketService';
import type { ColumnsType } from 'antd/es/table';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

const TicketList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [filteredTickets, setFilteredTickets] = useState<Ticket[]>([]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    loadTickets();
  }, []);

  useEffect(() => {
    filterTickets();
  }, [tickets, searchText, statusFilter]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const data = await ticketService.getTickets({ limit: 100 });
      
      // 安全检查：确保data是数组
      const safeData = Array.isArray(data) ? data : [];
      console.log('TicketList获取到的工单数据:', safeData);
      
      setTickets(safeData);
    } catch (error) {
      console.error('加载工单列表失败:', error);
      message.error('加载工单列表失败');
      setTickets([]); // 设置空数组避免崩溃
    } finally {
      setLoading(false);
    }
  };

  const filterTickets = () => {
    let filtered = tickets;

    // 状态筛选
    if (statusFilter) {
      filtered = filtered.filter(ticket => ticket.status === statusFilter);
    }

    // 搜索筛选
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(ticket => 
        ticket.title.toLowerCase().includes(searchLower) ||
        ticket.dms_ticket_code.toLowerCase().includes(searchLower) ||
        ticket.submitter_name.toLowerCase().includes(searchLower) ||
        (ticket.description && ticket.description.toLowerCase().includes(searchLower))
      );
    }

    setFilteredTickets(filtered);
  };

  const getStatusColor = (status: string) => {
    const colors = {
      pending: 'orange',
      approved: 'green',
      rejected: 'red',
      executed: 'blue',
      failed: 'red'
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  const getStatusText = (status: string) => {
    const texts = {
      pending: '待审批',
      approved: '已通过',
      rejected: '已拒绝',
      executed: '已执行',
      failed: '执行失败'
    };
    return texts[status as keyof typeof texts] || status;
  };

  const getPriorityColor = (priority: string) => {
    const colors = {
      low: 'blue',
      normal: 'default',
      high: 'orange',
      urgent: 'red'
    };
    return colors[priority as keyof typeof colors] || 'default';
  };

  const getPriorityText = (priority: string) => {
    const texts = {
      low: '低',
      normal: '普通',
      high: '高',
      urgent: '紧急'
    };
    return texts[priority as keyof typeof texts] || priority;
  };

  const columns: ColumnsType<Ticket> = [
    {
      title: '工单编号',
      dataIndex: 'dms_ticket_code',
      key: 'dms_ticket_code',
      width: 120,
      render: (code) => (
        <Tag color="blue" style={{ fontFamily: 'monospace' }}>
          {code}
        </Tag>
      ),
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (title, record) => (
        <Button 
          type="link" 
          style={{ padding: 0, height: 'auto', textAlign: 'left' }}
          onClick={() => navigate(`/tickets/${record.id}`)}
        >
          {title}
        </Button>
      ),
    },
    {
      title: '提交人',
      dataIndex: 'submitter_name',
      key: 'submitter_name',
      width: 100,
    },
    {
      title: '审批人',
      key: 'assignee',
      width: 100,
      render: (_, record) => record.assignee?.name || '未分配',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: 70,
      render: (priority) => (
        <Tag color={getPriorityColor(priority)} style={{ fontSize: '11px' }}>
          {getPriorityText(priority)}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (time) => new Date(time).toLocaleString(),
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      defaultSortOrder: 'descend',
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/tickets/${record.id}`)}
        >
          查看
        </Button>
      ),
    },
  ];

  return (
    <div className="page-container">
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col>
              <Title level={3} style={{ margin: 0 }}>
                工单列表
              </Title>
            </Col>
            <Col flex="auto">
              <Row gutter={[8, 8]} justify="end">
                <Col xs={24} sm={12} md={8}>
                  <Search
                    placeholder="搜索工单标题、编号、提交人..."
                    allowClear
                    onSearch={setSearchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </Col>
                <Col xs={12} sm={6} md={4}>
                  <Select
                    placeholder="状态筛选"
                    allowClear
                    style={{ width: '100%' }}
                    onChange={setStatusFilter}
                  >
                    <Option value="pending">待审批</Option>
                    <Option value="approved">已通过</Option>
                    <Option value="rejected">已拒绝</Option>
                    <Option value="executed">已执行</Option>
                    <Option value="failed">执行失败</Option>
                  </Select>
                </Col>
                <Col>
                  <Button 
                    icon={<ReloadOutlined />} 
                    onClick={loadTickets}
                    loading={loading}
                  >
                    刷新
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>

        <Table
          columns={columns}
          dataSource={filteredTickets}
          rowKey="id"
          loading={loading}
          pagination={{
            total: filteredTickets.length,
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
          }}
          scroll={{ x: 800 }}
          size="middle"
        />
      </Card>
    </div>
  );
};

export default TicketList;

